import axios from "axios";
import { publicApi } from "../api";
import {
  BillingOrderDetailsData,
  BillingReceiptError,
  BillingReceiptItem,
} from "../../interfaces/billing-order-details.interface";

const value = (...values: unknown[]) => values.find((item) => item !== null && item !== undefined && item !== "");
const text = (item: unknown) => typeof item === "string" || typeof item === "number" ? String(item).trim() || undefined : undefined;
const number = (item: unknown) => Number.isFinite(Number(item)) ? Number(item) : 0;
const array = (item: unknown): any[] => Array.isArray(item) ? item : [];
const path = (invoiceNumber: string, suffix = "") => `/api/public/receipt/${encodeURIComponent(invoiceNumber)}${suffix}`;

const mapItem = (item: any): BillingReceiptItem => ({
  name: text(value(item?.name, item?.productName, item?.Name)) || "Product",
  imageUrl: text(value(item?.imageUrl, item?.image, item?.productImageUrl)),
  quantity: number(value(item?.quantity, item?.qty, item?.invoicedQuantity)),
  unit: text(value(item?.unit, item?.measurementTypeCode, item?.unitName)),
  mrp: number(value(item?.mrp, item?.MRP)),
  sellingPrice: number(value(item?.sellingPrice, item?.unitPrice, item?.price)),
  discountAmount: number(value(item?.discountAmount, item?.discount)),
  lineTotal: number(value(item?.lineTotal, item?.totalAmount, item?.amount)),
});

const mapReceipt = (payload: any): BillingOrderDetailsData => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const summary = data?.summary || data?.invoiceSummary || {};
  const payment = data?.payment || {};
  const items = array(data?.items || data?.invoiceItems || data?.products);

  return {
    invoiceNumber: text(value(data?.invoiceNumber, data?.invoiceNo, data?.invoice?.invoiceNumber)) || "",
    orderNumber: text(value(data?.orderNumber, data?.orderNo)),
    invoiceDate: text(value(data?.invoiceDate, data?.invoicedDate, data?.date)),
    customer: {
      name: text(value(data?.customer?.name, data?.customerName, data?.userName)),
      maskedMobile: text(value(data?.customer?.maskedMobile, data?.maskedMobile, data?.primaryContactNbrMasked)),
    },
    store: {
      name: text(value(data?.store?.name, data?.storeName, data?.pos?.name)),
      address: text(value(data?.store?.address, data?.storeAddress, data?.pos?.address)),
    },
    legal: {
      cin: text(value(data?.legal?.cin, data?.cin)),
      gstin: text(value(data?.legal?.gstin, data?.gstin, data?.gstNumber)),
      fssaiLicenseNumber: text(value(data?.legal?.fssaiLicenseNumber, data?.fssaiLicenseNumber)),
    },
    payment: {
      mode: text(value(payment.mode, data?.paymentMode, data?.paymentModeName)),
      status: text(value(payment.status, data?.paymentStatus)),
      displayText: text(value(payment.displayText, payment.label)),
    },
    summary: {
      itemCount: number(value(summary.itemCount, data?.itemCount, items.length)),
      totalUnits: number(value(summary.totalUnits, data?.totalUnits)),
      taxableAmount: number(value(summary.taxableAmount, data?.taxableAmount)),
      discountAmount: number(value(summary.discountAmount, data?.discountAmount, data?.invoiceDiscount)),
      couponDiscountAmount: number(value(summary.couponDiscountAmount, data?.couponDiscountAmount)),
      rewardPointDiscountAmount: number(value(summary.rewardPointDiscountAmount, data?.rewardPointDiscountAmount)),
      deliveryCharge: number(value(summary.deliveryCharge, data?.deliveryCharge, data?.courierCharges)),
      taxAmount: number(value(summary.taxAmount, data?.taxAmount)),
      paymentGatewayCharges: number(value(summary.paymentGatewayCharges, data?.paymentGatewayCharges)),
      grossAmount: number(value(summary.grossAmount, data?.grossAmount, data?.totalAmount)),
      rewardPointsEarned: number(value(summary.rewardPointsEarned, data?.rewardPointsEarned)),
    },
    items: items.map(mapItem),
  };
};

export const getBillingOrderDetails = async (invoiceNumber: string) => {
  try {
    const response = await publicApi.get(path(invoiceNumber), { headers: { Accept: "application/json" } });
    const receipt = mapReceipt(response.data);
    if (!receipt.invoiceNumber) throw new BillingReceiptError("not-found");
    return receipt;
  } catch (error) {
    if (error instanceof BillingReceiptError) throw error;
    if (axios.isAxiosError(error) && [400, 404].includes(error.response?.status || 0)) {
      throw new BillingReceiptError("not-found");
    }
    throw new BillingReceiptError("error");
  }
};

export const downloadBillingInvoice = async (invoiceNumber: string): Promise<Blob> => {
  try {
    const response = await publicApi.get(path(invoiceNumber, "/invoice"), { responseType: "blob", headers: { Accept: "application/pdf" } });
    const blob = response.data as Blob;
    const pdfBlob = blob && blob.type && !blob.type.toLowerCase().includes("pdf")
      ? new Blob([blob], { type: "application/pdf" })
      : blob;
    return pdfBlob;
  } catch {
    throw new BillingReceiptError("error");
  }
};
